'use client'

import { useState, useEffect } from 'react'
import { ChevronRight, Check, AlertCircle } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { apiClient } from '@/lib/api/fetchWrapper'
import { Button } from '@/components/ui/button'

interface PromoCodeEffect {
  unlockedTicketIds?: string[]
  discountType?: 'percentage' | 'fixed'
  discountValue?: number
  appliesTo?: 'all' | string[] // 'all' or specific ticket IDs
}

interface PromoCodeState {
  code: string
  status: 'idle' | 'validating' | 'valid' | 'error'
  errorMessage?: string
  successMessage?: string
  effects?: PromoCodeEffect
}

interface PromoCodeInputProps {
  eventId?: string // Deprecated: use contentId instead
  contentId?: string // Generic content ID (event or course)
  contentType?: 'event' | 'course' // Type of content for API endpoint
  onPromoCodeValidated?: (effects: PromoCodeEffect, promocodeId?: string) => void
  initialCode?: string // For URL parameter support
  autoExpand?: boolean // Force expanded state (e.g. when all tickets are promo-only)
  className?: string
}

export default function PromoCodeInput({
  eventId,
  contentId,
  contentType = 'event',
  onPromoCodeValidated,
  initialCode,
  autoExpand = false,
  className = ''
}: PromoCodeInputProps) {
  // Support both eventId (deprecated) and contentId
  const resolvedContentId = contentId || eventId

  // Determine API path based on content type
  const getApiPath = () => {
    if (contentType === 'course') {
      return `/api/courses/${resolvedContentId}/validate-promo`
    }
    return `/api/event/${resolvedContentId}/validate-promo`
  }
  const { t, language } = useLanguage()
  const [isExpanded, setIsExpanded] = useState(!!initialCode || autoExpand)
  const [promoState, setPromoState] = useState<PromoCodeState>({
    code: initialCode || '',
    status: initialCode ? 'validating' : 'idle'
  })

  // Auto-validate if initial code is provided (from URL)
  useEffect(() => {
    if (initialCode && promoState.status === 'validating') {
      validatePromoCode(initialCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode])

  const validatePromoCode = async (code: string) => {
    if (!code.trim()) {
      setPromoState({
        code,
        status: 'error',
        errorMessage: t('event.registration.promocode.enterCode')
      })
      return
    }

    setPromoState(prev => ({ ...prev, status: 'validating' }))

    try {
      const data = await apiClient.post<{ valid: boolean; message?: string; effects?: PromoCodeEffect; promocodeId?: string }>(
        getApiPath(),
        {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: code.trim(), lang: language })
        }
      )

      if (!data.valid) {
        setPromoState({
          code,
          status: 'error',
          errorMessage: data.message || t('event.registration.promocode.invalid')
        })
        return
      }

      // Success!
      setPromoState({
        code,
        status: 'valid',
        successMessage: data.message,
        effects: data.effects
      })

      // Notify parent component
      if (onPromoCodeValidated && data.effects) {
        onPromoCodeValidated(data.effects, data.promocodeId)
      }
    } catch (error) {
      console.error('Promo validation error:', error)
      setPromoState({
        code,
        status: 'error',
        errorMessage: t('event.registration.promocode.validationError')
      })
    }
  }

  const handleApplyCode = () => {
    validatePromoCode(promoState.code)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApplyCode()
    }
  }

  const handleClearCode = () => {
    setPromoState({
      code: '',
      status: 'idle'
    })
    if (onPromoCodeValidated) {
      onPromoCodeValidated({}, undefined) // Clear effects and promocode ID
    }
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Collapsed/Expanded Toggle */}
      {!isExpanded && promoState.status !== 'valid' && (
        <Button
          variant="link"
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 p-0 h-auto group"
        >
          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          <span>{t('event.registration.promocode.haveCode')}</span>
        </Button>
      )}

      {/* Expanded Promo Code Input */}
      {(isExpanded || promoState.status === 'valid') && (
        <div className="space-y-3">
          {/* Input Field */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={promoState.code}
                onChange={(e) => setPromoState(prev => ({
                  ...prev,
                  code: e.target.value,
                  status: 'idle',
                  errorMessage: undefined,
                  successMessage: undefined
                }))}
                onKeyPress={handleKeyPress}
                onBlur={() => {
                  // Auto-validate on blur if code exists and hasn't been validated
                  if (promoState.code && promoState.status === 'idle') {
                    validatePromoCode(promoState.code)
                  }
                }}
                placeholder={t('event.registration.promocode.placeholder')}
                disabled={promoState.status === 'validating' || promoState.status === 'valid'}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              />

              {/* Status Icons */}
              {promoState.status === 'validating' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              )}

              {promoState.status === 'valid' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
              )}

              {promoState.status === 'error' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
            </div>

            {/* Apply/Clear Button */}
            {promoState.status !== 'valid' ? (
              <Button
                onClick={handleApplyCode}
                disabled={!promoState.code || promoState.status === 'validating'}
                className="min-w-[100px]"
              >
                {promoState.status === 'validating'
                  ? t('event.registration.promocode.validating')
                  : t('event.registration.promocode.apply')
                }
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={handleClearCode}
                className="min-w-[100px]"
              >
                {t('event.registration.promocode.remove')}
              </Button>
            )}
          </div>

          {/* Success Banner */}
          {promoState.status === 'valid' && promoState.successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    {promoState.successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Banner */}
          {promoState.status === 'error' && promoState.errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">{promoState.errorMessage}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
